import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { InviteUser, UpdateUserRole } from '@trellis/shared'

import {
  cancelInvitation,
  deactivateUser,
  inviteUser,
  reactivateUser,
  resetUserPassword,
  updateUserRole,
} from '@/requests/users'

function useInvalidateUsers() {
  const queryClient = useQueryClient()
  return async () => await queryClient.invalidateQueries({ queryKey: ['users'] })
}

export function useInviteUserMutation() {
  const invalidate = useInvalidateUsers()
  return useMutation({
    mutationFn: (data: InviteUser) => inviteUser(data),
    onSuccess: invalidate,
  })
}

export function useCancelInvitationMutation() {
  const invalidate = useInvalidateUsers()
  return useMutation({
    mutationFn: (id: string) => cancelInvitation(id),
    onSuccess: invalidate,
  })
}

export function useUpdateUserRoleMutation() {
  const invalidate = useInvalidateUsers()
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserRole }) =>
      updateUserRole(userId, data),
    onSuccess: invalidate,
  })
}

export function useDeactivateUserMutation() {
  const invalidate = useInvalidateUsers()
  return useMutation({
    mutationFn: (userId: string) => deactivateUser(userId),
    onSuccess: invalidate,
  })
}

export function useReactivateUserMutation() {
  const invalidate = useInvalidateUsers()
  return useMutation({
    mutationFn: (userId: string) => reactivateUser(userId),
    onSuccess: invalidate,
  })
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: (userId: string) => resetUserPassword(userId),
  })
}
